// @flow
import invariant from 'invariant';
import algoliaSearch from 'utils/algoliaSearch';
import { getJson } from 'utils/http';
import { arrToObject } from 'utils/functional';
import getPostSearchIndex from 'utils/search/getPostSearchIndex';
import getSearchConfig from 'utils/search/getSearchConfig';
import { postSearchFacetQueryFilters } from 'utils/search/getSearchQueryOptions';
import { trackEvent } from 'utils/analytics';
import type { Dispatch, State, GetState } from '..';
import type { PostSearchResults } from '../reducers/postSearch';

type SetQueryAction = { type: 'POST_SEARCH_SET_QUERY', query: string };
type SearchSuccessAction = {
  type: 'POST_SEARCH_RESULTS_SUCCESS',
  results: PostSearchResults
};
type SearchStartedAction = { type: 'POST_SEARCH_STARTED' };
type SetPageIndexAction = {
  type: 'POST_SEARCH_SET_PAGE_INDEX',
  pageIndex: number
};
type InitializeSearchAction = {
  type: 'POST_SEARCH_INITIALIZE',
  queryParams: Object
};
type ReinitializeSearchAction = {
  type: 'POST_SEARCH_REINITIALIZE'
};
type UninitializeSearchAction = {
  type: 'POST_SEARCH_UNINITIALIZE'
};
type ClearSearchResultsAction = {
  type: 'POST_SEARCH_CLEAR_RESULTS'
};
type ResetPageAction = {
  type: 'POST_SEARCH_RESET_PAGE'
};
type ResetFiltersAction = {
  type: 'POST_SEARCH_RESET_FILTERS'
};
type SetSearchFilter = {
  type: 'POST_SEARCH_SET_FILTER',
  key: string,
  value: ?string
};
type SelectFilterOption = {
  type: 'POST_SEARCH_SELECT_FILTER_OPTION',
  key: string,
  option: ?string
};
type DeselectFilterOption = {
  type: 'POST_SEARCH_DESELECT_FILTER_OPTION',
  key: string,
  option: ?string
};
type SelectAllFilterOption = {
  type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: string
};
type SetFacetOptions = {
  type: 'POST_SEARCH_SET_FACET_OPTIONS',
  facetKey: string,
  options: Array<string>,
  optionTitles: { [string]: string },
  optionsArray: Array<{
    id: string,
    parentId: ?string,
    slug: string,
    name: string
  }>,
  reload?: boolean
};

export type Action =
  | SetQueryAction
  | SearchSuccessAction
  | SearchStartedAction
  | SetPageIndexAction
  | InitializeSearchAction
  | ReinitializeSearchAction
  | UninitializeSearchAction
  | ClearSearchResultsAction
  | ResetPageAction
  | ResetFiltersAction
  | SelectAllFilterOption
  | DeselectFilterOption
  | SetSearchFilter
  | SelectFilterOption
  | SetFacetOptions;

const executeSearchQuery = ({
  state: {
    postSearch: {
      pageIndex: pageIndexState,
      query,
      searchId,
      filters,
      searchFacets
    }
  },
  pageIndex,
  subsiteId,
  includedSubsiteIds,
  locale
}: {
  state: State,
  pageIndex?: number,
  subsiteId: string,
  includedSubsiteIds: Array<string>,
  locale: string
}): Promise<*> => {
  const timestamp = new Date();

  const index = getPostSearchIndex();
  invariant(index, 'search index not retreived');

  pageIndex = pageIndex || pageIndexState;
  const searchFilters = postSearchFacetQueryFilters(
    filters,
    searchFacets,
    locale
  );
  const subsitesToSearch = [...includedSubsiteIds, subsiteId].map(
    id => `subsiteId:${id}`
  );
  const queryOptions = {
    index,
    hitsPerPage: 20,
    page: pageIndex || 0,
    facets: '*,subsiteId,pageType,categoryIds,locale',
    facetFilters: [subsitesToSearch],
    filters: searchFilters.join(' AND '),
    attributesToSnippet: ['description:20'],
    attributesToRetrieve: '*' // TODO: list attributes,
  };

  const searchConfig = getSearchConfig();

  return algoliaSearch(searchConfig, query, queryOptions).then(results => ({
    ...results,
    timestamp,
    searchId,
    // This is for analytics
    searchParameters: {
      query,
      queryOptions
    }
  }));
};

export const setSearchQuery = (query: string, didUserChange: boolean) => (
  dispatch: Dispatch
  // getState: GetState
) => {
  if (didUserChange) {
    trackEvent('Articles: Changed Search Query', {
      new_query: query
      // TODO: add tracking data for POST
      // ...getGradSchoolsSearchTrackingData(getState())
    });
  }
  dispatch({ type: 'POST_SEARCH_SET_QUERY', query });
};

export const loadDynamicFacetOptions = (
  subsiteId: string,
  reload?: boolean
) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const facetsThatArentLoaded = reload
    ? [...state.postSearch.facetsThatArentLoaded, 'articleCategoriesFacet']
    : state.postSearch.facetsThatArentLoaded;
  return Promise.all(
    [
      {
        fetchFn: () =>
          getJson(
            `/data/website/subsites/${subsiteId}/categories?lang=${USER_LOCALE}`
          ),
        facetKey: 'articleCategoriesFacet',
        responseToOptions: ({ categories }) => categories
      }
    ].map(({ facetKey, fetchFn, responseToOptions }) => {
      if (!facetsThatArentLoaded.includes(facetKey)) {
        return Promise.resolve();
      }
      return fetchFn()
        .then(response => responseToOptions(response))
        .then(options => {
          dispatch({
            type: 'POST_SEARCH_SET_FACET_OPTIONS',
            facetKey,
            options: options.map(f => f.slug),
            optionTitles: arrToObject(
              options.map(({ slug, name }) => [slug, name])
            ),
            optionsArray: options.map(({ slug, id, parentId, name }) => ({
              id,
              name,
              slug,
              parentId
            })),
            reload
          });
        });
    })
  );
};

export const initializeSearch = ({
  queryParams,
  subsiteId
}: {
  queryParams: Object,
  subsiteId: string
} = {}) => (dispatch: Dispatch) =>
  Promise.all([
    dispatch({
      type: 'POST_SEARCH_INITIALIZE',
      queryParams
    }),
    dispatch(loadDynamicFacetOptions(subsiteId))
  ]);

export const reinitializeSearch = ({
  subsiteId
}: {
  subsiteId: string
} = {}) => (dispatch: Dispatch) =>
  Promise.all([
    dispatch({
      type: 'POST_SEARCH_REINITIALIZE'
    }),
    dispatch(loadDynamicFacetOptions(subsiteId, true))
  ]);

export const uninitializeSearch = () => (dispatch: Dispatch) =>
  dispatch({ type: 'POST_SEARCH_UNINITIALIZE' });

export const setPageIndex = (pageIndex: number, didUserChange: boolean) => (
  dispatch: Dispatch
  // getState: GetState
) => {
  if (didUserChange) {
    trackEvent('POST: Changed Search Page Number', {
      new_page: pageIndex
      // TODO: tracking data
      // ...getGradSchoolsSearchTrackingData(getState())
    });
  }
  dispatch({ type: 'POST_SEARCH_SET_PAGE_INDEX', pageIndex });
};

const searchResultsSuccessful = ({
  results
}: {
  results: PostSearchResults
}): SearchSuccessAction => ({
  type: 'POST_SEARCH_RESULTS_SUCCESS',
  results
});

export const search = (
  subsiteId: string,
  includedSubsiteIds: Array<string>,
  locale: string
) => (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: 'POST_SEARCH_STARTED' });
  executeSearchQuery({
    state: getState(),
    subsiteId,
    includedSubsiteIds,
    locale
  }).then(results => {
    dispatch(searchResultsSuccessful({ results }));
  });
};

export const setSearchFilter = (
  filterKey: string,
  value: ?string
): SetSearchFilter => {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }
  return {
    type: 'POST_SEARCH_SET_FILTER',
    key: filterKey,
    value
  };
};

export const selectSearchFilterOption = (
  filterKey: string,
  option: ?string
): SelectFilterOption => ({
  type: 'POST_SEARCH_SELECT_FILTER_OPTION',
  key: filterKey,
  option
});

export const deselectSearchFilterOption = (
  filterKey: string,
  option: ?string
): DeselectFilterOption => ({
  type: 'POST_SEARCH_DESELECT_FILTER_OPTION',
  key: filterKey,
  option
});

export const selectAllSearchFilterOptions = (
  filterKey: string
): SelectAllFilterOption => ({
  type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: filterKey
});
export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'POST_SEARCH_RESET_FILTERS'
});
